import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Search } from "lucide-react";
import { api } from "@/lib/api";
import PartCard from "@/components/PartCard";

const CATEGORIES = ["All", "Engine", "Drivetrain", "Brakes", "Electrical", "Cooling", "Body", "Suspension", "Fuel System", "Accessories", "Interior", "Wheels & Tyres", "4x4 & Off-Road", "Glass"];
const CONDITIONS = ["All", "new", "used"];

export default function BrowsePage() {
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("All");
  const [condition, setCondition] = useState("All");

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api.get("/parts", { params: { limit: 200 } })
      .then((r) => mounted && setParts(r.data))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return parts.filter((p) => {
      if (category !== "All" && p.category !== category) return false;
      if (condition !== "All" && p.condition !== condition) return false;
      if (!term) return true;
      const hay = `${p.name} ${p.make} ${p.model} ${p.engine} ${p.oem_number} ${p.supplier_name}`.toLowerCase();
      return hay.includes(term);
    });
  }, [parts, q, category, condition]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <Helmet>
        <title>Browse SA vehicle parts inventory · Spannr</title>
        <meta name="description" content="Browse {parts.length}+ vehicle parts from scrapyards, dealerships and aftermarket suppliers across South Africa. Search by make, model, OEM number." />
        <link rel="canonical" href="https://spannr.co.za/browse" />
      </Helmet>
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold leading-none">
            Browse the SA parts network.
          </h1>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="flex items-center gap-2 bg-white border border-neutral-200 focus-within:border-[#FF5100] px-3 h-11 flex-1 min-w-[260px]">
          <Search size={16} className="text-neutral-500" />
          <input
            data-testid="browse-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by part, make, model, OEM, supplier..."
            className="bg-transparent outline-none text-sm flex-1 placeholder:text-neutral-400"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              data-testid={`filter-category-${c.toLowerCase()}`}
              onClick={() => setCategory(c)}
              className={`text-xs px-3 h-8 border transition-colors ${
                category === c
                  ? "border-[#FF5100] bg-[#FF5100]/10 text-[#FF5100]"
                  : "border-neutral-200 text-neutral-500 hover:border-neutral-300"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          {CONDITIONS.map((c) => (
            <button
              key={c}
              data-testid={`filter-condition-${c}`}
              onClick={() => setCondition(c)}
              className={`text-xs uppercase px-3 h-8 border transition-colors ${
                condition === c
                  ? "border-[#FF5100] bg-[#FF5100]/10 text-[#FF5100]"
                  : "border-neutral-200 text-neutral-500 hover:border-neutral-300"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-xs font-mono-tech text-neutral-500">[ Loading inventory... ]</div>
      ) : filtered.length === 0 ? (
        <div data-testid="browse-empty" className="text-sm text-neutral-500 py-12 text-center border border-neutral-200">
          No parts match your filters.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((p, i) => (
            <PartCard key={p.id} part={p} index={i} />
          ))}
        </div>
      )}
    </div>
  );
}
